import React, { useState, useEffect } from "react";
import { 
  Car, 
  Bell, 
  CalendarCheck, 
  CheckCircle2, 
  Star, 
  Clock, 
  MapPin, 
  ShieldCheck, 
  Camera,
  ArrowRight,
  Gauge
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export default function HowItWorks() {
  const [activeStep, setActiveStep] = useState(1);
  const [timerKey, setTimerKey] = useState(0);

  // Auto-advance through the 3 steps every 5 seconds, resetting cleanly on user click
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev === 3 ? 1 : prev + 1));
    }, 5000);
    return () => clearInterval(interval);
  }, [activeStep, timerKey]);

  const handleStepClick = (stepId) => {
    setActiveStep(stepId);
    setTimerKey((prev) => prev + 1);
  };

  const steps = [
    {
      id: 1,
      number: "01",
      tag: "INSTANT ONBOARDING",
      title: "Onboard Vehicle & Profile",
      description:
        "Scan your VIN or snap a photo of your car to instantly generate a tailored maintenance schedule, OEM recall history, and digital glovebox.",
      accent: "text-indigo-600",
      accentBg: "bg-indigo-50",
      accentBorder: "border-indigo-500",
      icon: <Car className="w-5 h-5 text-indigo-600" />,
    },
    {
      id: 2,
      number: "02",
      tag: "PREDICTIVE MONITORING",
      title: "Track & Get Smart Alerts",
      description:
        "Automatic mileage tracking and diagnostic intelligence forecast your service needs before breakdowns occur, with real-time maintenance reminders.",
      accent: "text-amber-600",
      accentBg: "bg-amber-50",
      accentBorder: "border-amber-500",
      icon: <Bell className="w-5 h-5 text-amber-600" />,
    },
    {
      id: 3,
      number: "03",
      tag: "1-TAP LOCAL BOOKING",
      title: "Book Trusted Local Service",
      description:
        "Compare certified shops in your neighborhood, view transparent upfront quotes, and secure instant appointments backed by service warranties.",
      accent: "text-emerald-600",
      accentBg: "bg-emerald-50",
      accentBorder: "border-emerald-500",
      icon: <CalendarCheck className="w-5 h-5 text-emerald-600" />,
    },
  ];

  return (
    <section
      id="howitworks"
      className="py-24 sm:py-32 relative bg-[#F9FAFB] border-t border-slate-100 overflow-hidden"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Section Header */}
        <motion.div 
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.5 }}
          className="text-center max-w-2xl mx-auto mb-16 sm:mb-20"
        >
          <span className="text-[11px] font-bold uppercase tracking-widest text-[#007AFF] mb-3 block">
            HOW AMERICAR WORKS
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900 mb-4">
            Simple, Seamless Vehicle Care
          </h2>
          <p className="text-sm md:text-base text-slate-500 font-normal leading-relaxed">
            From smart digital onboarding to guaranteed local service, experience complete automotive peace of mind in three effortless steps.
          </p>
        </motion.div>

        {/* 2-Column Product Showcase: Left Stepper Timeline + Right Live App Stage */}
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          
          {/* LEFT: Stepper Timeline with Dotted Line (Col 6) */}
          <div className="lg:col-span-6 relative">
            
            {/* The Delicate Animated Dotted Line Track */}
            <div className="absolute top-8 bottom-8 left-5 -translate-x-1/2 w-4 z-0 pointer-events-none overflow-hidden">
              <svg className="w-full h-full" preserveAspectRatio="none">
                <line
                  x1="50%"
                  y1="0"
                  x2="50%"
                  y2="100%"
                  stroke="#CBD5E1"
                  strokeWidth="2"
                  strokeDasharray="4 6"
                />
                <line
                  x1="50%"
                  y1="0"
                  x2="50%"
                  y2="100%"
                  stroke="#007AFF"
                  strokeWidth="2"
                  strokeDasharray="4 6"
                  className="animate-dash-flow opacity-70"
                />
              </svg>
            </div>

            {/* Stepper Cards */}
            <div className="space-y-6 sm:space-y-8 relative z-10">
              {steps.map((step) => {
                const isActive = activeStep === step.id;
                return (
                  <div
                    key={step.id}
                    onClick={() => handleStepClick(step.id)}
                    className={`flex items-start gap-4 sm:gap-6 p-4 sm:p-6 rounded-2xl transition-all duration-300 cursor-pointer ${
                      isActive
                        ? "bg-white shadow-[0_12px_35px_rgba(0,0,0,0.04)] border border-slate-100"
                        : "hover:bg-white/60 opacity-70 hover:opacity-100 border border-transparent"
                    }`}
                  >
                    {/* Step Node */}
                    <div
                      className={`flex-shrink-0 w-10 h-10 rounded-full bg-white border-2 font-bold text-xs flex items-center justify-center shadow-xs transition-all duration-300 ${
                        isActive
                          ? `${step.accentBorder} ${step.accent} scale-110 shadow-md shadow-blue-500/10`
                          : "border-slate-300 text-slate-400"
                      }`}
                    >
                      {step.number}
                    </div>

                    {/* Content */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className={`text-[10px] font-bold tracking-wider uppercase ${isActive ? step.accent : "text-slate-400"}`}>
                          {step.tag}
                        </span>
                      </div>
                      
                      <h3 className={`text-base sm:text-lg font-bold mb-1.5 transition-colors ${isActive ? "text-slate-900" : "text-slate-700"}`}>
                        {step.title}
                      </h3>
                      
                      <p className="text-xs sm:text-sm text-slate-500 font-normal leading-relaxed">
                        {step.description}
                      </p>

                      {/* Active Progress Bar Indicator */}
                      {isActive && (
                        <div className="w-full bg-slate-100 h-1 rounded-full mt-4 overflow-hidden">
                          <motion.div
                            key={`${activeStep}-${timerKey}`}
                            initial={{ width: "0%" }}
                            animate={{ width: "100%" }}
                            transition={{ duration: 5, ease: "linear" }}
                            className="h-full bg-[#007AFF]"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Micro Interaction Hint */}
            <div className="mt-6 text-left pl-14">
              <span className="text-[11px] text-slate-400 font-medium">
                ✨ Auto-advancing showcase • Click any step to inspect
              </span>
            </div>

          </div>

          {/* RIGHT: Live Interactive App Stage (Col 6) */}
          <div className="lg:col-span-6 flex justify-center">
            
            {/* Modern Phone Chassis */}
            <div className="relative w-full max-w-[340px] sm:max-w-[360px] bg-slate-900 p-3 sm:p-4 rounded-[42px] shadow-[0_25px_60px_-15px_rgba(0,122,255,0.22),0_15px_30px_rgba(0,0,0,0.12)] border-[4px] border-slate-800">
              
              {/* Camera Notch / Island */}
              <div className="w-24 h-4 bg-slate-950 rounded-full mx-auto mb-2 flex items-center justify-end pr-3">
                <div className="w-2 h-2 rounded-full bg-slate-800/80"></div>
              </div>

              {/* Inner Screen */}
              <div className="bg-[#F8FAFC] rounded-[32px] overflow-hidden p-5 flex flex-col justify-between min-h-[460px] border border-slate-100 relative">
                
                {/* Dynamic Screen Transition */}
                <AnimatePresence mode="wait">
                  
                  {/* SCREEN 1: VEHICLE ONBOARDING (VIN SCAN) */}
                  {activeStep === 1 && (
                    <motion.div
                      key="step1"
                      initial={{ opacity: 0, scale: 0.96, y: 10 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.96, y: -10 }}
                      transition={{ duration: 0.35, ease: "easeOut" }}
                      className="space-y-4"
                    >
                      <div className="flex items-center justify-between pb-2 border-b border-slate-200/60">
                        <span className="text-xs font-bold text-slate-900">Add Vehicle</span>
                        <span className="text-[10px] font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">
                          Step 1 of 3
                        </span>
                      </div>

                      {/* Animated Scanner Preview */}
                      <div className="relative bg-slate-900 rounded-2xl p-4 overflow-hidden border border-slate-800">
                        <div className="flex items-center justify-between text-slate-400 text-[10px] font-mono mb-2">
                          <span className="flex items-center gap-1">
                            <Camera className="w-3 h-3 text-[#007AFF]" /> SCANNING BARCODE
                          </span>
                          <span className="text-emerald-400">READY</span>
                        </div>

                        {/* Scanner Barcode Box */}
                        <div className="border border-dashed border-[#007AFF]/60 rounded-xl p-3 bg-slate-950/60 text-center relative overflow-hidden">
                          {/* Animated Scanning Laser Line */}
                          <motion.div 
                            animate={{ y: [0, 32, 0] }}
                            transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
                            className="h-[2px] w-full bg-gradient-to-r from-transparent via-[#007AFF] to-transparent shadow-[0_0_8px_#007AFF] absolute top-1 left-0"
                          />
                          <p className="font-mono text-xs tracking-widest text-slate-300 font-bold">
                            WAUZZZF46NA09214
                          </p>
                        </div>
                      </div>

                      {/* Recognized Vehicle Card */}
                      <motion.div 
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="bg-white rounded-2xl p-4 border border-slate-200/80 shadow-xs"
                      >
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-600">
                            <Car className="w-5 h-5" />
                          </div>
                          <div>
                            <h4 className="text-sm font-bold text-slate-900">2024 Audi A4 2.0T</h4>
                            <p className="text-[10px] text-slate-400 font-medium">Quattro Premium Plus</p>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-2 text-[11px] bg-slate-50 p-2.5 rounded-xl border border-slate-100">
                          <div>
                            <p className="text-slate-400 text-[9px] uppercase font-bold">Mileage</p>
                            <p className="font-bold text-slate-800">28,450 mi</p>
                          </div>
                          <div>
                            <p className="text-slate-400 text-[9px] uppercase font-bold">Status</p>
                            <p className="font-bold text-emerald-600 flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3" /> Verified
                            </p>
                          </div>
                        </div>
                      </motion.div>

                      <button className="w-full bg-[#007AFF] hover:bg-blue-600 text-white font-bold text-xs py-3 rounded-xl shadow-md shadow-blue-500/20 transition-all flex items-center justify-center gap-1.5">
                        Add to My Garage <ArrowRight className="w-3.5 h-3.5" />
                      </button>
                    </motion.div>
                  )}

                  {/* SCREEN 2: PREDICTIVE HEALTH & SMART ALERTS */}
                  {activeStep === 2 && (
                    <motion.div
                      key="step2"
                      initial={{ opacity: 0, scale: 0.96, y: 10 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.96, y: -10 }}
                      transition={{ duration: 0.35, ease: "easeOut" }}
                      className="space-y-4"
                    >
                      <div className="flex items-center justify-between pb-2 border-b border-slate-200/60">
                        <span className="text-xs font-bold text-slate-900">Live Health</span>
                        <span className="text-[10px] font-bold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                          Real-time AI
                        </span>
                      </div>

                      {/* Circular Vehicle Health Card */}
                      <div className="bg-white rounded-2xl p-4 border border-slate-200/80 shadow-xs flex items-center justify-between">
                        <div>
                          <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Health Score</p>
                          <h4 className="text-2xl font-black text-slate-900 mt-0.5">94<span className="text-xs text-slate-400 font-normal">/100</span></h4>
                          <span className="text-[10px] text-emerald-600 font-semibold flex items-center gap-1 mt-1">
                            ● All Core Systems Optimal
                          </span>
                        </div>
                        <div className="w-14 h-14 rounded-full bg-emerald-50 border-4 border-emerald-500/30 flex items-center justify-center text-emerald-600">
                          <Gauge className="w-6 h-6" />
                        </div>
                      </div>

                      {/* Smart Push Notification Pill */}
                      <motion.div 
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.15 }}
                        className="bg-amber-50/90 border border-amber-200/80 rounded-2xl p-3.5 shadow-sm"
                      >
                        <div className="flex items-start gap-2.5">
                          <div className="w-8 h-8 rounded-lg bg-amber-500 text-white flex items-center justify-center flex-shrink-0 mt-0.5 shadow-xs">
                            <Bell className="w-4 h-4 animate-bounce" />
                          </div>
                          <div>
                            <div className="flex items-center gap-1.5 mb-1">
                              <span className="text-[10px] font-bold text-amber-800 uppercase tracking-wide">Upcoming Service</span>
                              <span className="text-[9px] text-amber-600 bg-amber-100/80 px-1.5 rounded">In 350 mi</span>
                            </div>
                            <h5 className="text-xs font-bold text-slate-900">Brake Fluid & Pad Inspection</h5>
                            <p className="text-[10px] text-slate-600 mt-1 leading-snug">
                              Based on your 28,450 mileage and driving patterns, schedule your brake check this week.
                            </p>
                          </div>
                        </div>
                      </motion.div>

                      {/* Sub-system trackers */}
                      <div className="space-y-2">
                        <div className="bg-white p-2.5 rounded-xl border border-slate-100 flex items-center justify-between text-xs">
                          <span className="text-slate-600 font-medium text-[11px]">Synthetic Oil Life</span>
                          <span className="font-bold text-slate-900 text-[11px]">78% Good</span>
                        </div>
                        <div className="bg-white p-2.5 rounded-xl border border-slate-100 flex items-center justify-between text-xs">
                          <span className="text-slate-600 font-medium text-[11px]">Tire Pressure Monitor</span>
                          <span className="font-bold text-emerald-600 text-[11px]">All 35 PSI ✓</span>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {/* SCREEN 3: 1-TAP LOCAL BOOKING */}
                  {activeStep === 3 && (
                    <motion.div
                      key="step3"
                      initial={{ opacity: 0, scale: 0.96, y: 10 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.96, y: -10 }}
                      transition={{ duration: 0.35, ease: "easeOut" }}
                      className="space-y-4"
                    >
                      <div className="flex items-center justify-between pb-2 border-b border-slate-200/60">
                        <span className="text-xs font-bold text-slate-900">Local Booking</span>
                        <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full flex items-center gap-1">
                          <ShieldCheck className="w-3 h-3" /> Guaranteed
                        </span>
                      </div>

                      {/* Verified Mechanic Card */}
                      <div className="bg-white rounded-2xl p-4 border border-slate-200/80 shadow-xs">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-[9px] font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
                            TOP RATED PARTNER
                          </span>
                          <span className="text-xs font-bold text-slate-900">$89 Upfront</span>
                        </div>

                        <h4 className="text-sm font-bold text-slate-900">Precision Auto Care</h4>
                        
                        <div className="flex items-center gap-3 text-[10px] text-slate-500 mt-1 mb-3">
                          <span className="flex items-center gap-1 font-bold text-amber-500">
                            <Star className="w-3 h-3 fill-current" /> 4.9 (184)
                          </span>
                          <span>•</span>
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3 text-slate-400" /> 1.2 miles away
                          </span>
                        </div>

                        {/* Selected Appointment Slot */}
                        <div className="bg-slate-50 p-2.5 rounded-xl border border-slate-100 flex items-center justify-between text-xs">
                          <div className="flex items-center gap-2">
                            <Clock className="w-3.5 h-3.5 text-[#007AFF]" />
                            <span className="font-bold text-slate-800 text-[11px]">Tomorrow at 10:30 AM</span>
                          </div>
                          <span className="text-[9px] text-emerald-600 font-bold bg-emerald-50 px-1.5 py-0.5 rounded">
                            AVAILABLE
                          </span>
                        </div>
                      </div>

                      {/* Confirmation Stamp */}
                      <motion.div 
                        initial={{ opacity: 0, scale: 0.92 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.2 }}
                        className="bg-emerald-50/90 border border-emerald-200 rounded-2xl p-3 text-center"
                      >
                        <CheckCircle2 className="w-6 h-6 text-emerald-600 mx-auto mb-1" />
                        <h5 className="text-xs font-bold text-emerald-900">Appointment Confirmed</h5>
                        <p className="text-[10px] text-emerald-700 mt-0.5">
                          Synced with your calendar • Zero cancellation fees
                        </p>
                      </motion.div>

                      <button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs py-3 rounded-xl shadow-md shadow-emerald-500/20 transition-all flex items-center justify-center gap-1.5">
                        View Digital Pass <ArrowRight className="w-3.5 h-3.5" />
                      </button>
                    </motion.div>
                  )}

                </AnimatePresence>

              </div>
            </div>

          </div>

        </div>

      </div>
    </section>
  );
}
